import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Grafik.css";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ChartDataLabels);
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const Grafik = () => {
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState({
    overall: {
      berpenghuni: 0,
      tidakBerpenghuni: 0,
      layakHuni: 0,
      tidakLayakHuni: 0,
      backlog: 0
    },
    kecamatanData: [],
    backlogData: []
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  // References to chart containers for resizing
  const chartContainerRef1 = useRef(null);
  const chartContainerRef2 = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}/dataall`, { withCredentials: true });

        if (response.status === 200) {
          const data = response.data.dataTotal;
          
          // Extract overall data
          const overallData = data.find(item => item.statType === "overall");
          
          // Extract kecamatan data
          const kecamatanData = data.filter(item => item.statType === "kecamatan");
          
          // Extract backlog data
          const backlogData = data.filter(item => item.statType === "backlog");

          setStatistics({
            overall: overallData || {
              berpenghuni: 0,
              tidakBerpenghuni: 0,
              layakHuni: 0,
              tidakLayakHuni: 0,
              backlog: 0
            },
            kecamatanData,
            backlogData
          });
        } else {
          console.error("Error fetching data:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [navigate]);

  // Determine appropriate font and spacing sizes based on screen width
  const getFontSize = (baseSize) => {
    if (windowSize.width < 480) return baseSize * 0.6;
    if (windowSize.width < 768) return baseSize * 0.8;
    return baseSize;
  };

  // Optimize dataset visibility based on screen size
  const getDatasetsForView = () => {
    const isSmallScreen = windowSize.width < 768;
    const kecamatanLabels = statistics.kecamatanData.map(item => item.category);
    
    // For mobile, simplify to just the most important metrics
    if (isSmallScreen) {
      return [
        {
          label: "Rumah Layak Huni",
          data: statistics.kecamatanData.map(item => item.layakHuni),
          backgroundColor: "rgba(75, 192, 192, 0.8)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
        {
          label: "Rumah Tidak Layak Huni",
          data: statistics.kecamatanData.map(item => item.tidakLayakHuni),
          backgroundColor: "rgba(255, 99, 132, 0.8)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 1,
        },
        {
          label: "Total Backlog",
          data: statistics.kecamatanData.map(item => {
            // Find matching backlog entry for this kecamatan
            const backlogEntry = statistics.backlogData.find(
              backlog => backlog.category === item.category
            );
            return backlogEntry ? backlogEntry.backlog : item.backlog;
          }),
          backgroundColor: "rgba(153, 102, 255, 0.8)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 1,
        }
      ];
    }
    
    // Full data for larger screens
    return [
      {
        label: "Rumah Layak Huni",
        data: statistics.kecamatanData.map(item => item.layakHuni),
        backgroundColor: "rgba(75, 192, 192, 0.8)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: statistics.kecamatanData.map(item => item.tidakLayakHuni),
        backgroundColor: "rgba(255, 99, 132, 0.8)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
      {
        label: "Berpenghuni",
        data: statistics.kecamatanData.map(item => item.berpenghuni),
        backgroundColor: "rgba(54, 162, 235, 0.8)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
      {
        label: "Tidak Berpenghuni",
        data: statistics.kecamatanData.map(item => item.tidakBerpenghuni),
        backgroundColor: "rgba(255, 206, 86, 0.8)",
        borderColor: "rgba(255, 206, 86, 1)",
        borderWidth: 1,
      },
      {
        label: "Total Backlog",
        data: statistics.kecamatanData.map(item => {
          // Find matching backlog entry for this kecamatan
          const backlogEntry = statistics.backlogData.find(
            backlog => backlog.category === item.category
          );
          return backlogEntry ? backlogEntry.backlog : item.backlog;
        }),
        backgroundColor: "rgba(153, 102, 255, 0.8)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 1,
      }
    ];
  };

  const dataCombined = {
    labels: statistics.kecamatanData.map(item => item.category),
    datasets: getDatasetsForView()
  };

  // Configuration for optimized chart display
  const getChartOptions = (title, isTotalChart = false) => {
    const baseFontSize = getFontSize(14);
    const titleFontSize = getFontSize(16);
    const labelFontSize = getFontSize(12);
    
    const isSmallScreen = windowSize.width < 768;
    
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isSmallScreen ? "bottom" : "top",
          labels: {
            boxWidth: isSmallScreen ? 10 : 20,
            padding: isSmallScreen ? 5 : 10,
            font: { size: baseFontSize }
          }
        },
        title: {
          display: true,
          text: title,
          font: { size: titleFontSize, weight: 'bold' },
          padding: { top: 10, bottom: 15 }
        },
        datalabels: {
          display: isTotalChart || !isSmallScreen, // Always show labels on total chart
          anchor: 'end',
          align: 'top',
          font: { size: labelFontSize },
          color: '#444',
          formatter: (value) => value > 0 ? value : '',
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          padding: 10,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          titleFont: { size: baseFontSize },
          bodyFont: { size: baseFontSize },
          displayColors: true,
          callbacks: {
            title: (tooltipItems) => {
              return isTotalChart ? "Total Statistik" : `Kecamatan ${tooltipItems[0].label}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            font: { size: labelFontSize },
            maxRotation: isSmallScreen ? 90 : 45,
            minRotation: isSmallScreen ? 90 : 45,
          },
          grid: {
            display: false
          }
        },
        y: {
          ticks: { 
            font: { size: labelFontSize },
            callback: function(value) {
              // For smaller numbers, show the full value
              // For larger numbers, simplify to K format on mobile
              if (isSmallScreen && value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            }
          },
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        }
      },
      layout: {
        padding: {
          top: isSmallScreen ? 10 : 20
        }
      },
      animation: {
        duration: isSmallScreen ? 500 : 1000 // Faster animations on mobile
      }
    };
  };

  // Mobile-optimized data for total stats
  const getMobileFriendlyTotalsDatasets = () => {
    // Simplify and enhance contrast for mobile view
    return [
      {
        label: "Rumah Layak Huni",
        data: [statistics.overall.layakHuni],
        backgroundColor: "rgba(75, 192, 192, 0.9)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
      },
      {
        label: "Rumah Tidak Layak Huni",
        data: [statistics.overall.tidakLayakHuni],
        backgroundColor: "rgba(255, 99, 132, 0.9)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 2,
      },
      {
        label: "Total Backlog",
        data: [statistics.overall.backlog],
        backgroundColor: "rgba(153, 102, 255, 0.9)",
        borderColor: "rgba(153, 102, 255, 1)",
        borderWidth: 2,
      },
    ];
  };

  // For total stats
  const combinedTotals = {
    labels: ["Statistik Total"],
    datasets: windowSize.width < 768 
      ? getMobileFriendlyTotalsDatasets() 
      : [
          {
            label: "Rumah Layak Huni",
            data: [statistics.overall.layakHuni],
            backgroundColor: "rgba(75, 192, 192, 0.8)",
            borderColor: "rgba(75, 192, 192, 1)",
            borderWidth: 1,
          },
          {
            label: "Rumah Tidak Layak Huni",
            data: [statistics.overall.tidakLayakHuni],
            backgroundColor: "rgba(255, 99, 132, 0.8)",
            borderColor: "rgba(255, 99, 132, 1)",
            borderWidth: 1,
          },
          {
            label: "Berpenghuni",
            data: [statistics.overall.berpenghuni],
            backgroundColor: "rgba(54, 162, 235, 0.8)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
          {
            label: "Tidak Berpenghuni",
            data: [statistics.overall.tidakBerpenghuni],
            backgroundColor: "rgba(255, 206, 86, 0.8)",
            borderColor: "rgba(255, 206, 86, 1)",
            borderWidth: 1,
          },
          {
            label: "Total Backlog",
            data: [statistics.overall.backlog],
            backgroundColor: "rgba(153, 102, 255, 0.8)",
            borderColor: "rgba(153, 102, 255, 1)",
            borderWidth: 1,
          },
        ],
  };

  return (
    <div className="dashboard2">
      <main className="charts">
        {/* First Chart - Per Kecamatan */}
        <div className="chart-container" ref={chartContainerRef1}>
          <Bar 
            data={dataCombined} 
            options={getChartOptions("Statistik Rumah dan Backlog per Kecamatan")} 
            aria-label="Statistik per kecamatan"
          />
        </div>
        
        {/* Second Chart - Total Statistics - Always visible */}
        <div className="chart-container" ref={chartContainerRef2}>
          <Bar 
            data={combinedTotals} 
            options={getChartOptions("Total Statistik Rumah", true)} 
            aria-label="Statistik total"
          />
        </div>
      </main>
    </div>
  );
};